import React from 'react';
import { createUseStyles } from 'react-jss';
import Drawer from '@mui/material/Drawer';
import { useBreakpointDown } from '@shared/hooks/useBreakpoint';
import MenuIcon from '@mui/icons-material/Menu';

import { RoutePaths } from '../routes/RoutePaths';
import headerBackground from '@assets/header-background.png';
import { ThemeProps } from '@styles/theme';
import { Header } from '../../Header';
import { Groups } from '../../Groups';
import { GroupsHeading } from 'Groups/components/GroupsHeading';
// import { UsersSidebar } from 'Users/components/UsersSidebar';
import { ProceduresSidebar } from 'Procedures/components/ProceduresSidebar';
import { AnalyticsSidebar } from 'Analytics/components/AnalyticsSidebar';
import { useTheme } from '@mui/material/styles';
import { OrientationGuard } from '@shared/components/OrientationGuard';
import { PairDeviceModal } from 'AuthPart/DevicePairing/components/PairDeviceModal';

type AppLayoutProps = {
    children?: React.ReactNode;
    showSidebar?: boolean;
    path?: string;
}

const SIDEBAR_COMPONENTS = {
    // [RoutePaths.users]: <UsersSidebar />,
    [RoutePaths.procedures]: <ProceduresSidebar />,
    [RoutePaths.analytics]: <AnalyticsSidebar />,
    [RoutePaths.analyticsLegacy]: <AnalyticsSidebar />,
}

const useStyles = createUseStyles((theme: ThemeProps) => ({
    outerWrapper: {
        minHeight: '100dvh',
        fallbacks: { minHeight: '100vh' },
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: theme.colors.surface_page,
    },

    innerWrapper: {
        display: 'flex',
        flex: 1,
        minHeight: 0,
        minWidth: 0,
        width: '100%',
        margin: '0 auto',
        padding: `${0} ${theme.spacing(8)} ${theme.spacing(0)}`,
        [theme.down('desktop')]: {
            padding: `${0} ${theme.spacing(5)} ${theme.spacing(0)}`,
        },
        [theme.down('tablet')]: {
            padding: `${0} ${theme.spacing(4)} ${theme.spacing(0)}`,
        },
    },

    aside: {
        width: 202,
        flexShrink: 0,
        marginRight: theme.spacing(8),
        padding: `${theme.spacing(8)} ${0}`,
        [theme.down('desktop')]: {
            width: 176,
            marginRight: theme.spacing(5),
        },
    },

    mainContent: {
        width: '100%',
        minWidth: 0,
        // Allow the column to shrink below its content so pages that manage
        // their own inner scroll (e.g. NeoAnalytics) don't push the whole
        // document taller than the viewport. Pages that flow with the document
        // are unaffected (no overflow is imposed here).
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        paddingTop: theme.spacing(8),
        [theme.down('tablet')]: {
            paddingTop: theme.spacing(4),
        },
    },

    sidebarToggleRow: {
        display: 'flex',
        justifyContent: 'flex-start',
        marginBottom: theme.spacing(4),
    },

    sidebarToggleButton: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: theme.spacing(2),
        minHeight: 44,
        padding: `0 ${theme.spacing(4)}`,
        borderRadius: 22,
        border: `1px solid ${theme.colors.grey_light}`,
        backgroundColor: theme.colors.white,
        color: theme.colors.violet_primary,
        fontFamily: theme.font.primary,
        fontSize: 15,
        fontWeight: 600,
        cursor: 'pointer',
        transition: 'background .2s ease, border-color .2s ease',

        '&:hover': {
            borderColor: theme.colors.violet_primary,
            backgroundColor: theme.colors.transp_violet,
        },
    },

    drawerContent: {
        width: 'min(320px, 85vw)',
        height: '100%',
        padding: `${theme.spacing(4)} ${theme.spacing(4)} ${theme.spacing(6)}`,
        backgroundColor: theme.colors.white,
        overflowY: 'auto',
    },

    footer: {
        height: 64,
        backgroundColor: theme.colors.violet_darkest,
        backgroundImage: theme.isDark
            ? `linear-gradient(135deg, #0e0e1c 0%, #1a1235 50%, #0e1628 100%)`
            : `url(${headerBackground})`,
        backgroundSize: 'cover',
    },
}));

export const AppLayout: React.FC<AppLayoutProps> = ({
    children, showSidebar = true, path = '',
}) => {
    const theme = useTheme<ThemeProps>();
    const styles = useStyles({ theme });
    const isTabletLayout = useBreakpointDown('tablet');
    const isNeoAnalyticsPath = path === RoutePaths.usersNeoAnalytics || path === RoutePaths.groupNeoAnalytics || path === RoutePaths.proceduresNeoAnalytics;
    const [sidebarOpen, setSidebarOpen] = React.useState(false);

    React.useEffect(() => {
        if (!isTabletLayout) {
            setSidebarOpen(false);
        }
    }, [isTabletLayout]);

    const sidebarContent = (
        <>
            <GroupsHeading />
            {SIDEBAR_COMPONENTS[path]}
            <Groups />
        </>
    );

    return (
        <section className={styles.outerWrapper}>
            <Header />

            <section className={styles.innerWrapper}>
                {showSidebar && !isTabletLayout && (
                    <aside className={styles.aside}>
                        {sidebarContent}
                    </aside>
                )}

                <section className={styles.mainContent} style={isNeoAnalyticsPath ? { paddingTop: 0 } : undefined}>
                    {showSidebar && isTabletLayout && (
                        <div className={styles.sidebarToggleRow}>
                            <button
                                type="button"
                                className={styles.sidebarToggleButton}
                                onClick={() => setSidebarOpen(true)}
                            >
                                <MenuIcon fontSize="small" />
                                Open sidebar
                            </button>
                        </div>
                    )}
                    {children}
                </section>
            </section>
            {showSidebar && isTabletLayout && (
                <Drawer
                    anchor="left"
                    open={sidebarOpen}
                    onClose={() => setSidebarOpen(false)}
                >
                    <div className={styles.drawerContent}>
                        {sidebarContent}
                    </div>
                </Drawer>
            )}
            {/* <footer className={styles.footer} /> */}
            <OrientationGuard />

            {/* Lives here rather than inside the user menu so that /pair can open the same dialog,
                and so it survives navigation while a headset is waiting on a code. */}
            <PairDeviceModal />
        </section>
    )
}
